/**
 * Google Analytics 4 — strict GDPR-compliant initialization.
 *
 * GA4 is NOT loaded until the user explicitly grants consent via the cookie
 * banner. The helpers are silent no-ops until both:
 *   1. REACT_APP_GA_MEASUREMENT_ID is set, AND
 *   2. localStorage["rci_cookie_consent"] === "accepted".
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const CONSENT_KEY = "rci_cookie_consent";

let initialized = false;

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch (_) {
    return null;
  }
}

export function hasAnalyticsConsent() {
  return getConsent() === "accepted";
}

function loadGtag() {
  if (initialized || !MEASUREMENT_ID || typeof window === "undefined") return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false, // sent manually via trackPageView
  });
}

/**
 * Initialize analytics only if measurement ID is set AND the user has
 * granted explicit consent. Safe to call repeatedly.
 */
export function initAnalytics() {
  if (!hasAnalyticsConsent()) return;
  loadGtag();
}

/**
 * Called when the user clicks "Accept" in the cookie banner.
 * Persists consent and immediately bootstraps GA4 + sends a first page_view.
 */
export function grantAnalyticsConsent() {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CONSENT_KEY, "accepted");
  } catch (_) { /* noop */ }
  loadGtag();
  // Fire an initial page_view now that gtag has been loaded
  trackPageView(window.location.pathname + window.location.search);
}

/**
 * Called when the user clicks "Decline". Persists the refusal so the banner
 * doesn't reappear at every page load, and ensures GA4 stays silent.
 */
export function denyAnalyticsConsent() {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CONSENT_KEY, "declined");
  } catch (_) { /* noop */ }
  // Best-effort opt-out for any gtag instance that might already be in memory
  if (MEASUREMENT_ID && typeof window !== "undefined") {
    try {
      window[`ga-disable-${MEASUREMENT_ID}`] = true;
    } catch (_) { /* noop */ }
  }
}

/**
 * Clear consent so the banner shows again on next mount.
 */
export function resetAnalyticsConsent() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (_) { /* noop */ }
}

export function trackPageView(path) {
  if (!hasAnalyticsConsent() || !MEASUREMENT_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(name, params = {}) {
  if (!hasAnalyticsConsent() || !MEASUREMENT_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
}

export const isAnalyticsEnabled = () => Boolean(MEASUREMENT_ID);
